
import { Component, Ref, Vue } from 'vue-property-decorator'
import ProofreadLearningMapUnits from '@/components/templates/manage/ProofreadLearningMapUnits.vue'
import { GradeCode } from '@/const/GradeCode'
import { SubjectCode } from '@/const/SubjectCode'
import { SelectItem } from '@/models/SelectItem'
import { ProofreadLearningMapUnit } from '@/models/manage/ProofreadList'
import { SystemChartsApi } from '@/api/methods/SystemChartsApi'
import { SystemChartsApi as ProofreadSystemChartsApi } from '@/api/methods/proofread/SystemChartsApi'
/**
 * 校正画面-単元一覧画面(知識マップ)
 */
@Component({
  components: {
    ProofreadLearningMapUnits,
  },
})
export default class AcademyLearningMapUnits extends Vue {
  /** テンプレート情報 */
  @Ref()
  private proofreadLearningMapUnits!: ProofreadLearningMapUnits

  /** 読み込み完了フラグ */
  private isLoaded = false

  /** 教科コード初期値 */
  private defaultSubjectCode!: string

  /** 知識マップ一覧 */
  private filterMapList: SelectItem[] = []

  /** 知識マップ初期値 */
  private defaultMapIdKey = ''

  /** 知識マップ単元一覧 */
  private unitList: ProofreadLearningMapUnit[] = []

  /**
   * 学年一覧
   *
   * @returns 学年一覧
   */
  private get gradeList(): SelectItem[] {
    // 学年一覧の成形
    return GradeCode.LIST.map((grade) => {
      return {
        value: grade.code,
        label: grade.label,
      }
    })
  }

  /**
   * 教科一覧
   *
   * @returns 教科一覧
   */
  private get subjectList(): SelectItem[] {
    // 教科一覧の成形
    return SubjectCode.LIST.map((subject) => {
      return {
        value: subject.code,
        label: subject.label,
      }
    })
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // 教科初期値設定
    this.setDefaultSubjectCode()

    // 知識マップ一覧取得
    await this.loadSystemCharts(this.defaultSubjectCode)

    // 知識マップ情報が存在する場合、先頭項目を選択状態にして単元情報を取得
    if (this.filterMapList.length > 0) {
      this.defaultMapIdKey = this.filterMapList[0].value
      await this.loadCurriculumUnits(Number(this.defaultMapIdKey))
    }

    this.isLoaded = true

    this.$loading.complete()
  }

  /**
   * デフォルト教科設定
   */
  private setDefaultSubjectCode(): void {
    // クエリから教科コードを取得
    let querySubjectCode = this.$route.query.subject

    if (!querySubjectCode || typeof querySubjectCode !== 'string') {
      // 未指定時、指定不正時は空文字列に置換
      querySubjectCode = ''
    }

    // 定義情報から有効な教科情報を取得
    const subject = SubjectCode.findByCode(querySubjectCode)

    // 有効な学年情報をデフォルト値に設定(無効/未指定の場合はリスト先頭を使用)
    this.defaultSubjectCode = subject?.code || this.subjectList[0].value
  }

  /**
   * 知識マップ一覧を取得
   *
   * @param subjectCode 教科コード
   */
  private async loadSystemCharts(subjectCode: string): Promise<void> {
    const systemCharts = await SystemChartsApi.getSystemCharts(subjectCode)
    this.filterMapList = systemCharts.map((chart) => {
      return {
        value: `${chart.id}`,
        label: chart.name,
      }
    })
  }

  /**
   * 単元一覧取得
   *
   * @param chartId 系統図ID
   */
  private async loadCurriculumUnits(chartId: number): Promise<void> {
    // API実行
    const curriculumUnits = await ProofreadSystemChartsApi.getCurriculumUnits(chartId)
    this.unitList = curriculumUnits.map((unit) => {
      return {
        id: unit.id,
        name: unit.name,
        code: unit.code,
        groupName: unit.groupName,
        groupCode: unit.groupCode,
      }
    })
  }

  /**
   * 教科変更時のイベント処理
   */
  private async eventChangeSubject(): Promise<void> {
    // nullブロック
    if (!this.proofreadLearningMapUnits.subjectCode) {
      return
    }

    this.$loading.start()

    // 単元情報のクリア
    this.unitList = []

    // 知識マップ情報の取得
    await this.loadSystemCharts(this.proofreadLearningMapUnits.subjectCode)

    if (this.filterMapList.length == 0) {
      // 知識マップ情報が存在しない場合、選択中の単元をクリアして終了
      this.proofreadLearningMapUnits.mapFilter.setCurrentValue('')
      this.$loading.complete()
      return
    }

    // 知識マップ情報が存在する場合、先頭項目を選択状態に変更
    this.proofreadLearningMapUnits.mapFilter.setCurrentValue(this.filterMapList[0].value)

    this.$loading.complete()

    // 知識マップ変更イベントを呼び出す
    await this.eventChangeMap()
  }

  /**
   * 知識マップ変更時のイベント処理
   */
  private async eventChangeMap(): Promise<void> {
    // 知識マップIDが存在しない場合は終了
    if (!this.proofreadLearningMapUnits.mapId) {
      return
    }

    this.$loading.start()

    // 単元情報取得
    await this.loadCurriculumUnits(this.proofreadLearningMapUnits.mapId)

    this.$loading.complete()
  }

  /**
   * 単元選択時の処理
   *
   * @param id 単元ID
   */
  private async eventSelectUnit(id: number): Promise<void> {
    await this.$router.push(`/academy/map/${id}`)
  }
}
